import { ChatQuickAction, ChatQuickActions } from '@wundo/ui';
import { m } from '@/paraglide/messages.js';
import type { ChatQuickActionOffer } from '@/lib/chatPageOffer.ts';
import { chatQuickActionIcon } from '@/components/chatQuickActionIcon.tsx';

export interface ChatPageQuickActionsProps {
  /** What the page offers, in order. */
  actions: readonly ChatQuickActionOffer[];
  /** Whether the user has said something here, the send in flight included. */
  userHasSpoken: boolean;
  expanded: boolean;
  disabled: boolean;
  onSend: (text: string) => void;
  /** Hand the caret to the user. */
  onFocus: () => void;
  /** Stand the drawer where the page's opening question belongs. */
  onOpening: () => void;
}

/**
 * The pills above the composer: only what the page itself offers (DEV-16).
 *
 * Above the composer and outside the conversation, so they stay reachable once
 * it has started. Pills start a conversation, so they go once the user has
 * said something; on a phone they would otherwise sit above the keyboard. An
 * intake pill stays: it is the way back to the page's question.
 */
export function ChatPageQuickActions({
  actions,
  userHasSpoken,
  expanded,
  disabled,
  onSend,
  onFocus,
  onOpening,
}: ChatPageQuickActionsProps) {
  const shown = actions.filter(
    (action) => !userHasSpoken || action.behavior === 'intake',
  );

  if (shown.length === 0) {
    return null;
  }

  /*
   * A pill says the words on it, unless the page said otherwise: hand the caret
   * to the user, or first stand the drawer where the page's opening question
   * belongs — the way back for someone who read an older conversation since.
   */
  function run(action: ChatQuickActionOffer) {
    switch (action.behavior ?? 'send') {
      case 'send':
        onSend(action.text);
        return;
      case 'focus':
        onFocus();
        return;
      case 'intake':
        onOpening();
    }
  }

  return (
    <ChatQuickActions label={m.chat_quick_actions_label()} expanded={expanded}>
      {shown.map((action) => (
        <ChatQuickAction
          key={action.text}
          disabled={disabled}
          onClick={() => run(action)}
        >
          {chatQuickActionIcon(action.icon)}
          {action.text}
        </ChatQuickAction>
      ))}
    </ChatQuickActions>
  );
}
